import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { SETTINGS } from "@/constants/testIds";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    mobile: user?.mobile || "",
    designation: user?.designation || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.put("/users/me", form);
      updateUser(data?.data?.user || {});
      toast.success("Profile updated");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Profile" breadcrumbs={["Settings", "Profile"]}>
      <form onSubmit={submit} className="max-w-2xl space-y-6" data-testid={SETTINGS.profileRoot}>
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          {error && <Alert variant="destructive" className="mb-4"><AlertDescription>{error}</AlertDescription></Alert>}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">First name</Label>
              <Input value={form.first_name} onChange={set("first_name")} data-testid={SETTINGS.profileFirst} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Last name</Label>
              <Input value={form.last_name} onChange={set("last_name")} data-testid={SETTINGS.profileLast} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</Label>
              <Input value={user?.email || ""} disabled />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Mobile</Label>
              <Input value={form.mobile} onChange={set("mobile")} data-testid={SETTINGS.profileMobile} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Designation</Label>
              <Input value={form.designation} onChange={set("designation")} data-testid={SETTINGS.profileDesignation} />
            </div>
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0c135c] disabled:opacity-70"
          data-testid={SETTINGS.profileSave}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {loading ? "Saving…" : "Save changes"}
        </button>
      </form>
    </DashboardLayout>
  );
}
